/*
 * yotpo-schema-sync.js
 *
 * Keeps the Product structured data (schema.org JSON-LD) in agreement with the
 * review count and star rating actually shown on the page.
 *
 * The JSON-LD is rendered server-side from a cached Yotpo metafield, which runs
 * ahead of the live count visitors see (it aggregates grouped products and
 * counts reviews the public widget filters out). The visible widget, by
 * contrast, is painted from the live Yotpo bottomline response. Left alone the
 * two numbers never quite match, which reads as an inconsistency to search
 * engines. This module rewrites the JSON-LD's aggregateRating from the exact
 * values the widget displays, so structured data mirrors the page.
 *
 * applyAggregateRating() is a pure function (unit-tested). syncProductSchema()
 * is the thin DOM wrapper that call sites (ytp-reviews.js, the bundle reviews
 * script) invoke once they have the displayed count and score.
 */
(function () {
  'use strict';

  function isProductNode(node) {
    if (!node || typeof node !== 'object') return false;
    var type = node['@type'];
    return type === 'Product' || (Array.isArray(type) && type.indexOf('Product') !== -1);
  }

  // Return the Product node(s) inside a parsed JSON-LD document, whether it is a
  // bare Product object or wrapped in an @graph array.
  function findProductNodes(schema) {
    if (Array.isArray(schema && schema['@graph'])) {
      return schema['@graph'].filter(isProductNode);
    }
    return isProductNode(schema) ? [schema] : [];
  }

  /**
   * Update a parsed JSON-LD document in place so every Product node's
   * aggregateRating reflects the supplied review count / average score.
   *
   *   - total > 0  -> set reviewCount (and ratingValue when a valid score is
   *                   given), creating the aggregateRating object if absent.
   *   - otherwise  -> remove aggregateRating entirely, so no zero or empty
   *                   rating is ever published.
   *
   * Returns the same (mutated) document for convenience.
   */
  function applyAggregateRating(schema, total, avg) {
    var count = Number(total);
    var hasCount = isFinite(count) && count > 0;
    var score = Number(avg);
    var hasScore = isFinite(score) && score > 0;

    findProductNodes(schema).forEach(function (node) {
      if (!hasCount) {
        delete node.aggregateRating;
        return;
      }
      var rating = (node.aggregateRating && typeof node.aggregateRating === 'object')
        ? node.aggregateRating
        : {};
      rating['@type'] = 'AggregateRating';
      rating.reviewCount = Math.round(count);
      if (hasScore) {
        rating.ratingValue = score;
      }
      node.aggregateRating = rating;
    });

    return schema;
  }

  /**
   * Rewrite the on-page Product JSON-LD's aggregateRating to match the count and
   * score shown in the widget. Safe to call with a missing/zero count. Never
   * throws: any parse or DOM problem is logged and leaves the markup untouched.
   */
  function syncProductSchema(totalReviews, averageScore) {
    try {
      var el = document.getElementById('seo-schema-product');
      if (!el || el.dataset.locked === 'true') return;
      var schema = JSON.parse(el.textContent);
      applyAggregateRating(schema, totalReviews, averageScore);
      el.textContent = JSON.stringify(schema);
    } catch (err) {
      console.error('yotpo-schema-sync: unable to update product structured data', err);
    }
  }

  if (typeof window !== 'undefined') {
    window.syncProductSchema = syncProductSchema;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { applyAggregateRating: applyAggregateRating, syncProductSchema: syncProductSchema };
  }
})();
